import {APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, NgModule} from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';
import {RouteReuseStrategy} from '@angular/router';
import {IonicModule, IonicRouteStrategy, ModalController, Platform} from '@ionic/angular';
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {HTTP_INTERCEPTORS, HttpClient, HttpClientModule} from "@angular/common/http";
import {provideLottieOptions} from "ngx-lottie";
import player from 'lottie-web';
import {NgxSkeletonLoaderModule} from "ngx-skeleton-loader";
import {TranslateLoader, TranslateModule} from "@ngx-translate/core";
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import {AcceptLanguageInterceptor} from "./shared/interceptor/language.interceptor";
import {BasicAuthInterceptor} from "./shared/interceptor/basic-auth.interceptor";
import {SharedModule} from "./shared/shared.module";
import {TokenInterceptor} from "./shared/interceptor/token.interceptor";
import {UserService} from "./shared/service/user.service";
import {catchError, lastValueFrom, of, zip} from "rxjs";
import {AppVersionService} from "./shared/service/app-version.service";
import {UUIDInterceptor} from "./shared/interceptor/uuid.interceptor";
import {FormControlConverterPipe} from "./shared/pipe/form-control-converter.pipe";
import {ModalHeaderComponent} from "./shared/components/modal-header/modal-header.component";
import {ModalNavigatorModule} from "./modal-navigator/modal-navigator.module";
import {jwtDecode} from "jwt-decode";
import * as _ from 'lodash';
import {ServiceBoxIconComponent} from "./shared/components/service-box-icon/service-box-icon.component";
import {ContactDrawerComponent} from "./shared/components/contact-drawer/contact-drawer.component";
import {ProfileTypeInterceptor} from "./shared/interceptor/profile-type.interceptor";
import {FirebaseService} from "./shared/service/firebase.service";
import {ConsentModalComponent} from "./shared/components/consent-modal/consent-modal.component";
import {BrowserAnimationsModule} from "@angular/platform-browser/animations";

export function playerFactory() {
  return player;
}

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

export function initializeApp(userService: UserService,
                              appVersionService: AppVersionService,
                              platform: Platform) {

  return async () => {

    if (userService.token) {
      const decoded: any = jwtDecode(userService.token?.accessToken);
      if (!decoded?.user_id) {
        userService.logout();
      }

      userService.roles = decoded.roles;

    } else {

      if (!userService.user) {
        userService.generateGuestUser();
      }

    }

    const user$ = userService.token
      ? userService.getProfileMe().pipe(catchError(() => of(null)))
      : of(null);

    const system = platform.is('android') ? 'android' : 'ios';
    const checkAppVersion$ = appVersionService.activeVersion(system).pipe(catchError((error) => of(error)));

    const res$ = await lastValueFrom(zip([checkAppVersion$, user$]));
    const hasError = _.get(res$, '[0].error.type') == 'error';

    return res$;
  };
}


@NgModule({
  declarations: [
    AppComponent,
    ConsentModalComponent
  ],
  imports: [
    BrowserAnimationsModule,
    BrowserModule,
    IonicModule.forRoot(
      {mode: 'ios'}
    ),
    HttpClientModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    NgxSkeletonLoaderModule.forRoot({
      theme: {
        extendsFromRoot: true, // Permette la combinazione di temi
        height: '30px', // Attributo globale,
        background: 'linear-gradient(180deg, #F2F4F7 0%, #E6E9EF 100%)'
      },
    }),
    AppRoutingModule,
    SharedModule,
    FormControlConverterPipe,
    ModalHeaderComponent,
    ModalNavigatorModule,
    ServiceBoxIconComponent,
    ContactDrawerComponent
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [
    {provide: RouteReuseStrategy, useClass: IonicRouteStrategy},
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AcceptLanguageInterceptor,
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [UserService, AppVersionService, Platform, FirebaseService, ModalController],
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: BasicAuthInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ProfileTypeInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: UUIDInterceptor,
      multi: true
    },
    provideLottieOptions({
      player: playerFactory,
    })],
  bootstrap: [AppComponent],
})
export class AppModule {
}
